import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '@/components/ErrorAlert';
import { EventList } from '@/components/EventList';
import { PlainText } from '@/components/PlainText';
import { StatusBadge } from '@/components/StatusBadge';
import { diffLines } from '@/lib/diff';
import { errorText } from '@/lib/errors';
import { formatDateTime, ticketLabel } from '@/lib/format';
import { orpc, type client } from '@/lib/orpc';
import { useMe } from '@/lib/session';
import { decideWithPasskey, type DecisionType } from '@/lib/step-up';

export const Route = createFileRoute('/_app/tickets/$ref')({ component: TicketPage });

type Detail = Awaited<ReturnType<typeof client.tickets.get>>;
type Tab = 'thread' | 'proposals' | 'decisions' | 'notes' | 'log';
const ACTIVE = ['open', 'discussing', 'proposed', 'approved', 'in_progress', 'done', 'declined'];

function TicketPage() {
  const { t } = useTranslation();
  const { ref } = Route.useParams();
  const me = useMe();
  const [tab, setTab] = useState<Tab>('thread');
  // Live enough without a socket: the human reads, the assistants write.
  const ticket = useQuery({ ...orpc.tickets.get.queryOptions({ input: { ref } }), refetchInterval: 15_000 });

  if (ticket.isError) return <ErrorAlert error={ticket.error} onRetry={() => void ticket.refetch()} />;
  if (!ticket.data) return <span className="loading loading-spinner" />;
  const d = ticket.data;
  const isAssignee = d.assigneeId === me.id;
  const isParticipant = isAssignee || d.requesterId === me.id;
  const latest = d.proposals.at(-1);
  const waiting = d.needsHuman.map((id) => (id === d.requesterId ? d.requesterName : id === d.assigneeId ? d.assigneeName : id));

  return (
    <div className="space-y-6">
      <div>
        <Link to="/tickets" className="link text-sm">
          ← {t('nav.tickets')}
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="text-base-content/60 font-mono">{ticketLabel(d.number)}</span>
          <h1 className="text-2xl font-semibold" data-testid="ticket-title">
            {d.title}
          </h1>
          <StatusBadge status={d.status} />
          {d.priority !== 'normal' && <span className="badge badge-outline badge-sm">{t(`priority.${d.priority}`)}</span>}
        </div>
        <p className="text-base-content/70 mt-1 text-sm">
          {d.requesterName} → {d.assigneeName} · {formatDateTime(d.createdAt)}
          {(d.reopenedAfterDecline || d.reopenedAfterDone) && ` · ${t('ticket.reopenedOnce')}`}
        </p>
        <p className="text-base-content/50 mt-1 text-xs">{t('ticket.readOnly')}</p>
      </div>

      {d.status === 'paused' && (
        <div role="status" className="alert alert-error alert-soft">
          {t('ticket.paused', { reason: t(`pausedReason.${d.pausedReason ?? 'human'}`) })}
        </div>
      )}
      {waiting.length > 0 && <div className="alert alert-warning alert-soft text-sm">{t('ticket.waitingFor', { names: waiting.join(', ') })}</div>}

      <Decisions detail={d} canApprove={isAssignee && d.status === 'proposed' && !!latest} canPause={(isParticipant || me.role === 'admin') && ACTIVE.includes(d.status)} canResume={(isParticipant || me.role === 'admin') && d.status === 'paused'} />

      <section className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-base">{t('ticket.request')}</h2>
          <PlainText text={d.body} />
        </div>
      </section>

      <div role="tablist" className="tabs tabs-border">
        {(['thread', 'proposals', 'decisions', 'notes', 'log'] as const).map((k) => (
          <button key={k} type="button" role="tab" className={`tab ${tab === k ? 'tab-active' : ''}`} aria-selected={tab === k} onClick={() => setTab(k)}>
            {t(`ticket.${k}`)}
            {k === 'thread' && d.messages.length > 0 && <span className="badge badge-sm ml-2">{d.messages.length}</span>}
            {k === 'proposals' && d.proposals.length > 0 && <span className="badge badge-sm ml-2">{d.proposals.length}</span>}
          </button>
        ))}
      </div>
      {tab === 'thread' && <Thread detail={d} />}
      {tab === 'proposals' && <Proposals detail={d} />}
      {tab === 'decisions' && <DecisionHistory detail={d} />}
      {tab === 'notes' && <Notes detail={d} />}
      {tab === 'log' && <TicketLog ticketId={d.id} />}
    </div>
  );
}

function Thread({ detail: d }: { detail: Detail }) {
  const { t } = useTranslation();
  if (!d.messages.length) return <p className="text-base-content/60 text-sm">{t('ticket.noMessages')}</p>;
  return (
    <ol className="space-y-3" data-testid="thread">
      {d.messages.map((m) => {
        const human = m.authorId === d.requesterId ? d.requesterName : m.authorId === d.assigneeId ? d.assigneeName : m.authorName;
        return (
          <li key={m.id} className={`card bg-base-100 shadow-sm ${m.authorId === d.assigneeId ? 'ml-0 md:ml-12' : 'mr-0 md:mr-12'}`}>
            <div className="card-body gap-2 p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium">{t('ticket.from', { name: m.authorName, actor: t(`actor.${m.actorKind}`) })}</span>
                <span className="badge badge-ghost badge-xs">{t(`kind.${m.kind}` as never, { defaultValue: m.kind })}</span>
                {m.relaysHuman && (
                  <span className="badge badge-info badge-soft badge-xs" title={t('ticket.relaysHumanHint')}>
                    {t('ticket.relaysHuman', { name: human })}
                  </span>
                )}
                <span className="text-base-content/50 ml-auto">{formatDateTime(m.createdAt)}</span>
              </div>
              <PlainText text={m.body} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Proposals({ detail: d }: { detail: Detail }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(d.proposals.length - 1);
  const [showDiff, setShowDiff] = useState(true);
  if (!d.proposals.length) return <p className="text-base-content/60 text-sm">{t('ticket.noProposals')}</p>;
  const p = d.proposals[Math.min(selected, d.proposals.length - 1)]!;
  const prev = d.proposals.find((x) => x.version === p.version - 1);
  return (
    <section className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <div className="flex flex-wrap items-center gap-2">
          {d.proposals.map((x, i) => (
            <button key={x.id} type="button" className={`btn btn-xs ${i === selected ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSelected(i)}>
              {t('ticket.version', { version: x.version })}
              {i === d.proposals.length - 1 && ` (${t('ticket.current')})`}
            </button>
          ))}
          {prev && (
            <button type="button" className="btn btn-xs btn-outline ml-auto" onClick={() => setShowDiff(!showDiff)}>
              {showDiff ? t('ticket.showFull') : t('ticket.showDiff')}
            </button>
          )}
        </div>
        <p className="text-base-content/60 text-xs">
          {p.authorName} · {formatDateTime(p.createdAt)} · SHA-256 <span className="font-mono">{p.sha256.slice(0, 12)}…</span>
        </p>
        {prev && showDiff ? <Diff before={prev.body} after={p.body} since={prev.version} /> : <PlainText text={p.body} />}
      </div>
    </section>
  );
}

function Diff({ before, after, since }: { before: string; after: string; since: number }) {
  const { t } = useTranslation();
  const lines = diffLines(before, after);
  if (!lines) {
    return (
      <div>
        <p className="text-base-content/70 mb-1 text-xs">{t('ticket.diffTooLarge')}</p>
        <PlainText text={after} />
      </div>
    );
  }
  return (
    <div>
      <p className="text-base-content/70 mb-1 text-xs">{t('ticket.changesSince', { version: since })}</p>
      <pre className="bg-base-200 rounded-box overflow-auto p-2 text-xs whitespace-pre-wrap" data-testid="diff">
        {lines.map((l, i) => (
          <div key={i} className={l.op === 'add' ? 'bg-success/20' : l.op === 'del' ? 'bg-error/20 line-through' : ''}>
            {l.op === 'add' ? '+ ' : l.op === 'del' ? '- ' : '  '}
            {l.text}
          </div>
        ))}
      </pre>
    </div>
  );
}

function DecisionHistory({ detail: d }: { detail: Detail }) {
  const { t } = useTranslation();
  if (!d.approvals.length) return <p className="text-base-content/60 text-sm">{t('ticket.noDecisions')}</p>;
  const versionOf = (id: string) => d.proposals.find((p) => p.id === id)?.version ?? 0;
  return (
    <ul className="space-y-2">
      {d.approvals.map((a) => (
        <li key={a.id} className="bg-base-100 rounded-box p-3 text-sm shadow-sm">
          <span className={a.decision === 'approved' ? 'text-success font-medium' : 'text-error font-medium'}>
            {a.decision === 'approved' ? t('ticket.approvedBy', { name: a.decidedByName }) : t('ticket.rejectedBy', { name: a.decidedByName })}
          </span>{' '}
          {t('ticket.forVersion', { version: versionOf(a.proposalId) })} · {formatDateTime(a.createdAt)}
          {a.note && <PlainText text={a.note} className="text-base-content/80 mt-1" />}
        </li>
      ))}
    </ul>
  );
}

function Notes({ detail: d }: { detail: Detail }) {
  const { t } = useTranslation();
  if (!d.notes.length) return <p className="text-base-content/60 text-sm">{t('ticket.noNotes')}</p>;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {d.notes.map((n) => (
        <section key={n.userId} className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <p className="text-xs font-medium">
              {n.userName} · {formatDateTime(n.updatedAt)}
            </p>
            <PlainText text={n.body} />
          </div>
        </section>
      ))}
    </div>
  );
}

function TicketLog({ ticketId }: { ticketId: string }) {
  const log = useQuery(orpc.events.list.queryOptions({ input: { ticketId, limit: 200 } }));
  if (log.isError) return <ErrorAlert error={log.error} />;
  if (!log.data) return <span className="loading loading-spinner" />;
  return <EventList items={log.data.items} showTicket={false} />;
}

/** The four human-only actions (PLAN.md §1), each confirmed with a passkey. */
function Decisions({ detail: d, canApprove, canPause, canResume }: { detail: Detail; canApprove: boolean; canPause: boolean; canResume: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [type, setType] = useState<DecisionType | null>(null);
  const [note, setNote] = useState('');
  const latest = d.proposals.at(-1);
  const previous = latest ? d.proposals.find((p) => p.version === latest.version - 1) : undefined;
  const requesterMessages = d.messages.filter((m) => m.authorId === d.requesterId && m.actorKind === 'assistant');
  const decide = useMutation({
    mutationFn: () => decideWithPasskey(d.id, type!, { note, ...(latest && (type === 'approve' || type === 'reject') && { proposal: { id: latest.id, sha256: latest.sha256 } }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: orpc.tickets.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.events.key() });
      dialog.current?.close();
    }
  });
  if (!canApprove && !canPause && !canResume) return null;
  const open = (next: DecisionType) => {
    setType(next);
    setNote('');
    decide.reset();
    dialog.current?.showModal();
  };
  const title =
    type === 'approve' ? t('ticket.decideApprove', { version: latest?.version ?? 0 }) : type === 'reject' ? t('ticket.decideReject', { version: latest?.version ?? 0 }) : type === 'pause' ? t('ticket.decidePause') : t('ticket.decideResume');

  return (
    <div className="flex flex-wrap gap-2" data-testid="decisions">
      {canApprove && (
        <>
          <button type="button" className="btn btn-success btn-sm" onClick={() => open('approve')}>
            {t('ticket.approve')}
          </button>
          <button type="button" className="btn btn-outline btn-error btn-sm" onClick={() => open('reject')}>
            {t('ticket.reject')}
          </button>
        </>
      )}
      {canPause && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => open('pause')}>
          {t('ticket.pause')}
        </button>
      )}
      {canResume && (
        <button type="button" className="btn btn-primary btn-sm" onClick={() => open('resume')}>
          {t('ticket.resume')}
        </button>
      )}
      <dialog ref={dialog} className="modal" aria-labelledby="decision-title">
        <div className="modal-box max-w-3xl">
          <h3 id="decision-title" className="text-lg font-semibold">
            {title}
          </h3>
          {(type === 'approve' || type === 'reject') && latest ? (
            <div className="mt-3 space-y-3">
              <p className="text-base-content/70 text-sm">{t('ticket.decideLead', { sha: latest.sha256.slice(0, 12) })}</p>
              <p className="text-base-content/70 text-sm">{t('ticket.relayStats', { relayed: requesterMessages.filter((m) => m.relaysHuman).length, total: requesterMessages.length })}</p>
              <div className="max-h-80 overflow-auto">{previous ? <Diff before={previous.body} after={latest.body} since={previous.version} /> : <PlainText text={latest.body} />}</div>
              <label className="block">
                <span className="mb-1 block text-sm">{t('ticket.note')}</span>
                <textarea className="textarea w-full" rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            </div>
          ) : (
            <p className="text-base-content/70 mt-3 text-sm">{t('ticket.decidePauseLead')}</p>
          )}
          {decide.isError && <p className="text-error mt-3 text-sm">{decide.error instanceof Error && decide.error.name === 'NotAllowedError' ? t('ticket.decisionFailed') : errorText(t, decide.error)}</p>}
          <div className="modal-action">
            <button type="button" className="btn btn-ghost" onClick={() => dialog.current?.close()}>
              {t('common.cancel')}
            </button>
            <button type="button" className={`btn ${type === 'reject' ? 'btn-error' : 'btn-primary'}`} disabled={decide.isPending} onClick={() => decide.mutate()}>
              {decide.isPending && <span className="loading loading-spinner loading-sm" />}
              {t('ticket.confirm')}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button type="submit">{t('common.close')}</button>
        </form>
      </dialog>
    </div>
  );
}
